import React from 'react'
import Radium from 'radium'
import { Row, Col, FormGroup, ControlLabel, FormControl, Button } from 'react-bootstrap'
import { assetUrl, calculateAmountWithCharge, printNumber, valueOrUndefined } from '../common'
import {
  PostalCodeElement,
  CardNumberElement,
  CardExpiryElement,
  CardCVCElement
} from 'react-stripe-elements'
import { RegionDropdown } from 'react-country-region-selector'
import propTypeSchema from 'react-json-schema-proptypes'
import { Address, Card } from '../types'
import ReCAPTCHA from "react-google-recaptcha"

const schema = {
  'type': 'object',
  'properties': {
    'type': { 'enum': ['alpha'] },
    'addressLabel': { 'type': 'string' },
    'cityLabel': { 'type': 'string' },
    'stateLabel': { 'type': 'string' },
    'zipCodeLabel': { 'type': 'string' },
    'creditCardLabel': { 'type': 'string' },
    'expirationDateLabel': { 'type': 'string' },
    'cvcLabel': { 'type': 'string' },
    'checkboxesTextColor': { 'type': 'string' },
    'donateButtonBorderColor': { 'type': 'string' },
    'donateButtonColor': { 'type': 'string' },
    'donateButtonLabel': { 'type': 'string' },
    'donateButtonBorderRadius': { 'type': 'string' },
    'titleColor': { 'type': 'string' },
    'mobileBreakPoint': { 'type': 'number' },
    'mobileMaxWidth': { 'type': 'number' },
    'showSaveInfo': { 'type': 'boolean' }
  }
}

const requiredError = (field) => ({ code: 'required', message: `${field} required` })
const recaptchaRef = React.createRef();

@Radium
export default class AlphaPaymentInfo extends React.PureComponent {
  static propSchema = schema

  static propTypes = propTypeSchema(schema, {
    'required': ['onAddressChanged', 'onAssumeFeeChanged', 'onDonateClicked', 'onSaveInfoChanged', 'canDonate', 'onValidChanged', 'onCardChanged'],
    'properties': {
      'displayErrors': { 'type': 'boolean' },
      'canDonate': { 'type': 'boolean' },
      'address': Address.Schema,
      'onAddressChanged': { 'isFunction': true },
      'onAssumeFeeChanged': { 'isFunction': true },
      'onDonateClicked': { 'isFunction': true },
      'onSaveInfoChanged': { 'isFunction': true },
      'onValidChanged': { 'isFunction': true },
      'onCardChanged': { 'isFunction': true },
      'validForm': { 'type': 'boolean' },
      'saveInfo': { 'type': 'boolean' },
      'saveInfoDisabled': { 'type': 'boolean' },
      'saveInfoLabel': { 'type': 'string' },
      'hideSaveInfo': { 'type': 'boolean' },
      'hideAssumeFee': { 'type': 'boolean' },
      'assumeFee': { 'type': 'boolean' },
      'assumeFeeLabel': { 'type': 'string' },
      'totalAmountLabel': { 'type': 'string' },
      'cards': { 'type': 'array', 'items': Card.Schema },
      'selectedCard': Card.Schema,
      'recaptchaSiteKey': { 'type': 'string' },
      'validRecaptcha': {'type': 'boolean' },
      'recaptchaChanged': { 'isFunction': true },
      'isFailed': { 'type': 'boolean' }
    }
  })

  static defaultProps = {
    addressLabel: 'Street Address',
    cityLabel: 'City',
    stateLabel: 'State',
    zipCodeLabel: 'ZIP',
    creditCardLabel: 'Credit Card Number',
    expirationDateLabel: 'Expiration',
    cvcLabel: 'CVC',
    donateButtonLabel: 'Give Now',
    checkboxesTextColor: '#48484D',
    donateButtonBorderColor: '#E61844',
    donateButtonColor: '#E61844',
    donateButtonBorderRadius: '9px',
    showSaveInfo: true,
    mobileBreakPoint: 768,
    mobileMaxWidth: 900,
    hideSaveInfo: false,
    saveInfoLabel: 'Save my payment information\nfor future donations',
    hideAssumeFee: false,
    assumeFeeLabel: 'I\'d like to cover the processing fee so 100%\nof my donation goes to help',
    totalAmountLabel: 'Total Donation:'
  }

  constructor(props) {
    super(props)
    this.state = {
      formErrors: {
        street: requiredError('Address'),
        city: requiredError('City'),
        zipCode: requiredError('ZIP code'),
        cardNumber: requiredError('Credit card number'),
        expiration: requiredError('Expiration date'),
        cvc: requiredError('CVC')
      },
      errorMessage: requiredError('Credit card number')
    }
  }

  assumeFeeClicked = (e) => {
    this.props.onAssumeFeeChanged(e.target.checked)
  }

  saveInfoClicked = (e) => {
    this.props.onSaveInfoChanged(e.target.checked)
  }

  handleAddressChange = (e) => {
    this.props.onAddressChanged({
      [e.target.name]: e.target.value
    })
  }

  handleRegionChange = (value) => {
    this.props.onAddressChanged({
      state: value
    })
  }

  handleCardChanged = (e) => {
    const value = e.target.value
    if (value === -1) {
      this.props.onCardChanged(null)
    } else {
      this.props.onCardChanged(this.props.cards.find(c => c.id === value))
    }
  }

  handleElementChange = (e) => {
    switch (e.elementType) {
      case 'postalCode':
        const prevZipError = this.state.formErrors.zipCode
        this.setState(state => {
          return {
            formErrors: {
              ...state.formErrors,
              zipCode: e.error ? e.error : (!e.complete ? requiredError('ZIP code') : undefined)
            }
          }
        }, () => {
          if (prevZipError !== this.state.formErrors.zipCode) {
            this.updateErrorMessage()
            this.props.onValidChanged(this.allValid())
          }
        })
        this.props.onAddressChanged({
          zipCode: e.value
        })
        break
      case 'cardNumber':
        const prevCardError = this.state.formErrors.cardNumber
        this.setState(state => {
          return {
            formErrors: {
              ...state.formErrors,
              cardNumber: e.error ? e.error : (!e.complete ? requiredError('Credit card number') : undefined)
            }
          }
        }, () => {
          if (prevCardError !== this.state.formErrors.cardNumber) {
            this.updateErrorMessage()
            this.props.onValidChanged(this.allValid())
          }
        })
        break
      case 'cardExpiry':
        const prevExpirationError = this.state.formErrors.expiration
        this.setState(state => {
          return {
            formErrors: {
              ...state.formErrors,
              expiration: e.error ? e.error : (!e.complete ? requiredError('Expiration date') : undefined)
            }
          }
        }, () => {
          if (prevExpirationError !== this.state.formErrors.expiration) {
            this.updateErrorMessage()
            this.props.onValidChanged(this.allValid())
          }
        })
        break
      case 'cardCvc':
        const prevCvcError = this.state.formErrors.cvc
        this.setState(state => {
          return {
            formErrors: {
              ...state.formErrors,
              cvc: e.error ? e.error : (!e.complete ? requiredError('CVC') : undefined)
            }
          }
        }, () => {
          if (prevCvcError !== this.state.formErrors.cvc) {
            this.updateErrorMessage()
            this.props.onValidChanged(this.allValid())
          }
        })
    }
  }

  updateErrorMessage = () => {
    let error = null
    if (this.state.formErrors.street) {
      error = this.state.formErrors.street
    } else if (this.state.formErrors.city) {
      error = this.state.formErrors.city
    } else if (this.state.formErrors.zipCode) {
      error = this.state.formErrors.zipCode
    } else if (this.state.formErrors.cardNumber) {
      error = this.state.formErrors.cardNumber
    } else if (this.state.formErrors.expiration) {
      error = this.state.formErrors.expiration
    } else if (this.state.formErrors.cvc) {
      error = this.state.formErrors.cvc
    }

    this.setState({
      errorMessage: error
    })
  }

  validateField = (fieldName, value) => {
    let fieldErrors = {}
    switch (fieldName) {
      case 'street':
        fieldErrors.street = value && value.length > 0 ? null : requiredError
        break
      case 'city':
        fieldErrors.city = (value && value.length > 0) ? null : requiredError
        break
    }

    this.setState(state => {
      return {
        formErrors: {
          ...state.formErrors,
          ...fieldErrors
        }
      }
    }, () => {
      this.updateErrorMessage()
      this.props.onValidChanged(this.allValid())
    })
  }

  donateClicked = () => {
    this.props.onDonateClicked()
  }

  allValid = () => {
    return ((!this.state.formErrors.street &&
      !this.state.formErrors.address &&
      !this.state.formErrors.city &&
      !this.state.formErrors.state &&
      !this.state.formErrors.zipCode &&
      !this.state.formErrors.cardNumber &&
      !this.state.formErrors.expiration &&
      !this.state.formErrors.cvc) || !!this.props.selectedCard) && this.props.validRecaptcha
  }

  componentDidMount() {
    this.props.onValidChanged(this.allValid())
    if (this.props.hideAssumeFee) {
      this.props.onAssumeFeeChanged(false)
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.address !== this.props.address) {
      if (prevProps.address.street !== this.props.address.street)
        this.validateField('street', this.props.address.street)
      if (prevProps.address.city !== this.props.address.city)
        this.validateField('city', this.props.address.city)
    }

    if (prevProps.selectedCard !== this.props.selectedCard) {
      this.props.onValidChanged(this.allValid())
    }

    if (prevProps.recaptchaToken !== this.props.recaptchaSiteKey) {
      this.props.onValidChanged(this.allValid())
    }

    if (!prevProps.isFailed && this.props.isFailed) {
      recaptchaRef.current.reset()
      this.props.recaptchaChanged(null)
    }

    if (prevProps.amount !== this.props.amount) {
      this.updateAmount()
    }
  }

  updateAmount = () => {
    const amountSpan = document.querySelector('.amountCol span')
    if (amountSpan) {
      const parentSize = amountSpan.parentElement.parentElement.parentElement.getBoundingClientRect().width 
      if (amountSpan.textContent.length >= 10 && parentSize <= 290) {
        amountSpan.style.fontSize = "0.8em"
      } else {
        amountSpan.style = {}
      }
    } 
  }

  submitRecaptcha = (value) => {
    this.props.recaptchaChanged(value)
  }

  render() {
    const styles = {
      input: {
        base: {
          fontFamily: 'Roboto, sans-serif',
          fontSize: '22px',
          color: '#555'
        },
        invalid: {
          color: '#555'
        }
      },
      dropdown: {
        background: `url(${assetUrl('dropdown-1.png')}) 100% / 40px no-repeat #FFFFFF`,
        fontFamily: 'Roboto, sans-serif',
        fontSize: '16px',
        color: '#555',
        paddingBottom: '10px'
      },
      checkboxesText: {
        color: this.props.checkboxesTextColor
      },
      checked: {
        backgroundImage: `url(${assetUrl('checkmark.png')})`
      },
      donateButton: {
        borderColor: this.props.donateButtonBorderColor,
        backgroundColor: this.props.donateButtonColor,
        borderRadius: this.props.donateButtonBorderRadius
      },
      donateButtonDisabled: {
        borderRadius: this.props.donateButtonBorderRadius
      },
      title: {
        color: valueOrUndefined(this.props, 'titleColor')
      },
      breakPointStyle: {}
    }
    if (this.props.mobileMaxWidth) {
      styles['breakPointStyle'][`@media (max-width: ${this.props.mobileBreakPoint - 1}px)`] = {
        maxWidth: this.props.mobileMaxWidth,
        marginLeft: "auto",
        marginRight: "auto"
      }
    }
    return (
      <div className='alpha' style={[styles.breakPointStyle]}>
        <Row>
          <Col xs={12} className='title' style={styles.title}>
            Payment Information
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            {this.props.cards.length > 0 &&
              <div>
                <FormGroup className='infoEntry' controlId='email'>
                  <ControlLabel>Payment Options</ControlLabel>
                  <FormControl
                    componentClass='select'
                    placeholder='Use a previous card'
                    onChange={this.handleCardChanged}
                    style={styles.dropdown}>
                    {this.props.cards.map(card => <option
                      value={card.id}>{card.brand} ending
                    in {card.last4}</option>).concat(new Array(<option value={-1}>Use a different card</option>))}
                  </FormControl>
                </FormGroup>
              </div>
            }
            {(this.props.cards.length === 0 || !this.props.selectedCard) &&
              <div>
                <Row>
                  <Col xs={12}>
                    <FormGroup
                      className={'infoEntry' + ((this.state.formErrors.street && this.props.displayErrors) ? ' has-error' : '')}>
                      <ControlLabel>
                        {this.props.addressLabel}<span> *</span>
                      </ControlLabel>
                      <FormControl name='street' id='street' type='text' value={this.props.address.street}
                        onChange={this.handleAddressChange} />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col xs={4}>
                    <FormGroup
                      className={'infoEntry' + ((this.state.formErrors.city && this.props.displayErrors) ? ' has-error' : '')}>
                      <ControlLabel>
                        {this.props.cityLabel}<span> *</span>
                      </ControlLabel>
                      <FormControl name='city' id='city' type='text' value={this.props.address.city}
                        onChange={this.handleAddressChange} />
                    </FormGroup>
                  </Col>
                  <Col xs={4}>
                    <FormGroup
                      className={'infoEntry'}>
                      <ControlLabel>
                        {this.props.stateLabel}<span> *</span>
                      </ControlLabel>
                      <RegionDropdown
                        id={'RegionDropdown'}
                        style={styles.dropdown}
                        country={'United States'}
                        value={this.props.address.state}
                        onChange={this.handleRegionChange}
                        showDefaultOption={false}
                        labelType='short'
                        valueType='short'
                      />
                    </FormGroup>
                  </Col>
                  <Col xs={4}>
                    <FormGroup
                      className={'infoEntry' + ((this.state.formErrors.zipCode && this.props.displayErrors) ? ' has-error' : '')}>
                      <ControlLabel>
                        {this.props.zipCodeLabel}<span> *</span>
                      </ControlLabel>
                      <div id='zipCode' className='inputContainer'>
                        <PostalCodeElement style={styles.input} onChange={this.handleElementChange} placeholder='' />
                      </div>
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col xs={12}>
                    <FormGroup
                      className={'infoEntry' + ((this.state.formErrors.cardNumber && this.props.displayErrors) ? ' has-error' : '')}
                    >
                      <ControlLabel>
                        {this.props.creditCardLabel}<span> *</span>
                      </ControlLabel>
                      <div id='cardNumber'
                        className={'inputContainer'}>
                        <CardNumberElement
                          className='creditCard'
                          style={styles.input}
                          onChange={this.handleElementChange} placeholder='' />
                      </div>
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col xs={6}>
                    <FormGroup
                      className={'infoEntry' + ((this.state.formErrors.expiration && this.props.displayErrors) ? ' has-error' : '')}
                    >
                      <ControlLabel>
                        {this.props.expirationDateLabel}<span> *</span>
                      </ControlLabel>
                      <div id='expireDate'
                        className={'inputContainer'}>
                        <CardExpiryElement style={styles.input} onChange={this.handleElementChange} />
                      </div>
                    </FormGroup>
                  </Col>
                  <Col xs={6}>
                    <FormGroup
                      className={'infoEntry' + ((this.state.formErrors.cvc && this.props.displayErrors) ? ' has-error' : '')}
                    >
                      <ControlLabel>
                        {this.props.cvcLabel}<span> *</span>
                      </ControlLabel>
                      <div id='cvc' className={'inputContainer'}>
                        <CardCVCElement style={styles.input} onChange={this.handleElementChange} placeholder='' />
                      </div>
                    </FormGroup>
                  </Col>
                </Row>
                {this.props.displayErrors && this.state.errorMessage &&
                  <Row>
                    <Col xs={12} className='infoEntry has-error'>
                      <label className='textCenter'>{this.state.errorMessage.message}</label>
                    </Col>
                  </Row>
                }
              </div>
            }
          </Col>
        </Row>
        {!this.props.selectedCard && this.props.showSaveInfo && !this.props.hideSaveInfo &&
          <Row>
            <Col xs={12}>
              <div className={'save-check'} style={styles.checkboxesText}>
                <input
                  id='saveInfo'
                  name='saveInfo'
                  type='checkbox'
                  disabled={this.props.saveInfoDisabled} checked={this.props.saveInfo}
                  onChange={this.saveInfoClicked} />
                <label htmlFor='saveInfo' style={this.props.saveInfo ? [styles.checked] : []} />
                <span className='checkbox'>{this.props.saveInfoLabel}</span>
              </div>
            </Col>
          </Row>
        }
        {!this.props.hideAssumeFee &&
        <Row>
          <Col xs={12}>
            <div className={'fees-check'} style={styles.checkboxesText}>
              <input
                id='assumeFee'
                name='assumeFee'
                type='checkbox'
                checked={this.props.assumeFee}
                onChange={this.assumeFeeClicked}/>
              <label htmlFor='assumeFee' style={this.props.assumeFee ? [styles.checked] : []}/>
              <span
                className='checkbox'>{this.props.assumeFeeLabel}</span>
            </div>
          </Col>
        </Row>
        }
        <div className={'totalDonation'}>
          <Row>
            <Col sm={8} xs={6}>
              <span>{this.props.totalAmountLabel}</span>
            </Col>
            <Col className='amountCol rightAlign' xs={6} sm={4}>
              <span>${printNumber(this.props.assumeFee ? calculateAmountWithCharge(this.props.amount ? this.props.amount.amount : 0) : (this.props.amount ? this.props.amount.amount : 0))}</span>
            </Col>
          </Row>
        </div>
        {this.props.displayErrors && !this.props.validForm &&
          <Row className={'errorMessageRow'}>
            <Col xs={12} className={'errorMessage'}>
              <Col xs={1}>
                <div className={'exclamation'} />
              </Col>
              <Col xs={11} className={'errorContainer'}>
                <div className={'errorText'}> There was a problem processing your request.</div>
              </Col>
            </Col>
          </Row>
        }
        <div>
          <Row className={'rowPaddingRecaptcha'}>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={this.props.recaptchaSiteKey}
              onChange={this.submitRecaptcha}
            />
          </Row>
          <Row>
            <Col xs={12}>
              <Button
                id='donateButton'
                className={'donateButton'}
                bsSize='large'
                disabled={!this.props.canDonate || !this.props.validRecaptcha}
                style={this.props.canDonate && this.props.validRecaptcha ? styles.donateButton : styles.donateButtonDisabled}
                onClick={this.donateClicked}>
                {this.props.donateButtonLabel}
              </Button>
              <br />
            </Col>
          </Row>
        </div>
      </div>
    )
  }
}
